import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { ConfirmActionDialog } from '@/components/platform/ConfirmActionDialog';
import { StateBadge } from '@/components/platform/StateBadge';
import { Checkbox } from '@/components/ui/Checkbox';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { useAsyncData } from '@/hooks';
import { gradingStrategiesApi } from '@/api/config-governance';
import {
  GRADING_MODELS,
  GRADING_SCALES,
  INSTITUTION_TYPES,
  MISSING_RESULT_RULES,
  ROUNDING_MODES,
  type GradingModel,
  type GradingScale,
  type GradingStrategy,
  type MissingResultRule,
  type RoundingMode,
  type ValidationReport,
} from '@/types/config-governance';
import { validateStrategyDraft, type StrategyIssue } from './grading-validation';
import { BandScale } from './BandScale';
import styles from './platform-pages.module.css';
import gov from './governance.module.css';

/**
 * One grading strategy: metadata, the score range, and THE BAND EDITOR.
 *
 * The editor keeps its numbers as strings while they are being typed — "1."
 * is a legitimate keystroke on the way to "1.5", and a number-typed state
 * would snap it away — and parses once per render for the two consumers that
 * need numbers: the scale drawing and the live validation mirror. The mirror
 * (validateStrategyDraft) runs on every keystroke and renders in the
 * operator's language; the server's validate endpoint remains the authority
 * and is called after every save, its sentences shown verbatim.
 *
 * Drafts only. An ACTIVE or SUPERSEDED strategy is the ruler marks were read
 * against, so the page shows it read-only and offers exactly one action:
 * draft the next version (a create with the same code and scope — the server
 * derives version max+1 and refuses a second open draft with a sentence this
 * page relays).
 */

interface BandRow {
  code: string;
  label: string;
  min: string;
  max: string;
  gradePoints: string;
  description: string;
}

interface HonoursRow {
  classification: string;
  minAverage: string;
}

interface DraftState {
  name: string;
  description: string;
  gradingModel: GradingModel;
  gradingScale: GradingScale;
  minScore: string;
  maxScore: string;
  passThreshold: string;
  decimalPlaces: string;
  rounding: RoundingMode;
  missingResultRule: MissingResultRule;
  bands: BandRow[];
  honours: HonoursRow[];
  applicableInstitutionTypes: string[];
}

const toDraft = (strategy: GradingStrategy): DraftState => ({
  name: strategy.name,
  description: strategy.description ?? '',
  gradingModel: strategy.gradingModel,
  gradingScale: strategy.gradingScale,
  minScore: String(strategy.minScore),
  maxScore: String(strategy.maxScore),
  passThreshold: String(strategy.passThreshold),
  decimalPlaces: String(strategy.decimalPlaces),
  rounding: strategy.rounding,
  missingResultRule: strategy.missingResultRule,
  bands: strategy.bands.map((band) => ({
    code: band.code,
    label: band.label,
    min: String(band.minInclusive),
    max: String(band.maxInclusive),
    gradePoints: band.gradePoints === null ? '' : String(band.gradePoints),
    description: band.description ?? '',
  })),
  honours: (strategy.honours ?? []).map((rule) => ({
    classification: rule.classification,
    minAverage: String(rule.minAverage),
  })),
  applicableInstitutionTypes: strategy.applicableInstitutionTypes,
});

const parse = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

export function GradingStrategyDetailPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { strategyId = '' } = useParams();

  const fetcher = useCallback(() => gradingStrategiesApi.get(strategyId), [strategyId]);
  const { state, reload, replace } = useAsyncData<GradingStrategy>(
    `grading-strategy:${strategyId}`,
    fetcher,
  );

  return (
    <div className={styles.page}>
      <nav className={styles.breadcrumb} aria-label={t('studio.breadcrumbLabel')}>
        <Link to="/admin/grading-strategies">{t('grading.title')}</Link>
        <Icon name="chevronRight" size={14} aria-hidden="true" />
        <span>{state.status === 'ready' ? state.data.code : '…'}</span>
      </nav>

      <AsyncPanel state={state} onRetry={reload}>
        {(strategy) => (
          // Keyed on the id only: a save replaces the held strategy, and a
          // remount on updatedAt would wipe the server report the save just
          // fetched. The editor resyncs through its own effect instead.
          <StrategyView key={strategy.id} strategy={strategy} replace={replace} reload={reload} onDeleted={() => navigate('/admin/grading-strategies')} toast={toast} />
        )}
      </AsyncPanel>
    </div>
  );
}

function StrategyView({
  strategy,
  replace,
  reload,
  onDeleted,
  toast,
}: {
  strategy: GradingStrategy;
  replace: (next: GradingStrategy) => void;
  reload: () => void;
  onDeleted: () => void;
  toast: ReturnType<typeof useToast>;
}) {
  const { t } = useTranslation();
  const editable = strategy.status === 'DRAFT';
  const [draft, setDraft] = useState<DraftState>(() => toDraft(strategy));
  const [saving, setSaving] = useState(false);
  const [serverReport, setServerReport] = useState<ValidationReport | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [redrafting, setRedrafting] = useState(false);

  // A save replaces the held strategy; the editor must not keep showing the
  // pre-save text over it.
  useEffect(() => setDraft(toDraft(strategy)), [strategy]);

  const shape = useMemo(
    () => ({
      gradingModel: draft.gradingModel,
      gradingScale: draft.gradingScale,
      minScore: parse(draft.minScore),
      maxScore: parse(draft.maxScore),
      passThreshold: parse(draft.passThreshold),
      decimalPlaces: Number.parseInt(draft.decimalPlaces, 10) || 0,
      bands: draft.bands.map((band) => ({
        code: band.code,
        label: band.label,
        minInclusive: parse(band.min),
        maxInclusive: parse(band.max),
        gradePoints: band.gradePoints.trim() === '' ? null : parse(band.gradePoints),
      })),
      honours: draft.honours.map((rule) => ({
        classification: rule.classification,
        minAverage: parse(rule.minAverage),
      })),
    }),
    [draft],
  );

  const numbersReadable =
    Number.isFinite(shape.minScore) &&
    Number.isFinite(shape.maxScore) &&
    Number.isFinite(shape.passThreshold) &&
    shape.bands.every(
      (band) =>
        Number.isFinite(band.minInclusive) &&
        Number.isFinite(band.maxInclusive) &&
        (band.gradePoints === null || Number.isFinite(band.gradePoints)),
    );

  const issues: StrategyIssue[] = useMemo(
    () => (numbersReadable ? validateStrategyDraft(shape) : []),
    [numbersReadable, shape],
  );

  const set = <K extends keyof DraftState>(key: K, value: DraftState[K]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const setBand = (index: number, patch: Partial<BandRow>) =>
    setDraft((current) => ({
      ...current,
      bands: current.bands.map((band, i) => (i === index ? { ...band, ...patch } : band)),
    }));

  const setHonours = (index: number, patch: Partial<HonoursRow>) =>
    setDraft((current) => ({
      ...current,
      honours: current.honours.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)),
    }));

  const save = async () => {
    setSaving(true);
    setServerReport(null);
    try {
      const updated = await gradingStrategiesApi.update(strategy.id, {
        name: draft.name.trim(),
        description: draft.description.trim() || undefined,
        gradingModel: draft.gradingModel,
        gradingScale: draft.gradingScale,
        minScore: shape.minScore,
        maxScore: shape.maxScore,
        passThreshold: shape.passThreshold,
        decimalPlaces: shape.decimalPlaces,
        rounding: draft.rounding,
        missingResultRule: draft.missingResultRule,
        bands: shape.bands.map((band, index) => ({
          ...band,
          description: draft.bands[index]?.description.trim() || null,
        })),
        honours: shape.honours,
        applicableInstitutionTypes: draft.applicableInstitutionTypes,
      });
      replace(updated);
      // The authority speaks after every save: the server re-runs the same
      // checks the mirror approximates, and its verdict is the one shown as
      // "the server says".
      const report = await gradingStrategiesApi.validate(strategy.id);
      setServerReport(report);
      toast.success(t('grading.savedToast', { code: updated.code, n: updated.version }));
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  };

  const activate = async (reason: string) => {
    void reason; // Recorded nowhere server-side: activation has no reason field.
    setSaving(true);
    setActivateError(null);
    try {
      const activated = await gradingStrategiesApi.activate(strategy.id);
      replace(activated);
      setActivating(false);
      toast.success(t('grading.activatedToast', { code: activated.code, n: activated.version }));
    } catch (cause) {
      // The 400 body's `errors` array is not carried by ApiError, so fetch
      // the identical list from the validate endpoint and show it in the
      // report panel — same checks, same sentences.
      setActivateError(cause instanceof Error ? cause.message : String(cause));
      try {
        setServerReport(await gradingStrategiesApi.validate(strategy.id));
      } catch {
        /* The refusal sentence alone still explains the situation. */
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setDeleteError(null);
    try {
      await gradingStrategiesApi.remove(strategy.id);
      toast.success(t('grading.deletedToast', { code: strategy.code, n: strategy.version }));
      onDeleted();
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : String(cause));
      setSaving(false);
    }
  };

  const draftNextVersion = async () => {
    setRedrafting(true);
    try {
      const created = await gradingStrategiesApi.create({
        code: strategy.code,
        name: strategy.name,
        description: strategy.description ?? undefined,
        gradingModel: strategy.gradingModel,
        gradingScale: strategy.gradingScale,
        minScore: strategy.minScore,
        maxScore: strategy.maxScore,
        passThreshold: strategy.passThreshold,
        decimalPlaces: strategy.decimalPlaces,
        rounding: strategy.rounding,
        missingResultRule: strategy.missingResultRule,
        bands: strategy.bands.map((band) => ({ ...band })),
        honours: strategy.honours ?? undefined,
        reassessmentRule: strategy.reassessmentRule ?? undefined,
        applicableInstitutionTypes: strategy.applicableInstitutionTypes,
        ...(strategy.institutionId ? { institutionId: strategy.institutionId } : {}),
      });
      toast.success(t('grading.redraftedToast', { code: created.code, n: created.version }));
      window.location.assign(`/admin/grading-strategies/${encodeURIComponent(created.id)}`);
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : String(cause));
      setRedrafting(false);
    }
  };

  const scaleTitle = t('grading.scale.aria', {
    name: draft.name,
    min: draft.minScore,
    max: draft.maxScore,
    threshold: draft.passThreshold,
    bands: draft.bands.length,
  });

  return (
    <>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{strategy.name}</h1>
          <div className={styles.rowMetaLine}>
            <code className={styles.codeTag}>{strategy.code}</code>
            <span className={styles.mutedTag}>{t('grading.versionShort', { n: strategy.version })}</span>
            <StateBadge kind="lifecycle" value={strategy.status} />
            <span className={gov.scopeTag}>
              {strategy.institutionId === null ? t('grading.scopePlatform') : t('grading.scopeInstitution')}
            </span>
          </div>
        </div>
        <div className={gov.inlineActions}>
          {editable ? (
            <>
              <button type="button" className={styles.secondaryBtn} onClick={() => setDeleting(true)} disabled={saving}>
                {t('grading.deleteDraft')}
              </button>
              <button type="button" className={styles.secondaryBtn} onClick={save} disabled={saving}>
                {t('grading.saveDraft')}
              </button>
              <button type="button" className={styles.primaryBtn} onClick={() => setActivating(true)} disabled={saving}>
                <Icon name="check" size={16} />
                {t('grading.activate')}
              </button>
            </>
          ) : (
            <button type="button" className={styles.primaryBtn} onClick={draftNextVersion} disabled={redrafting}>
              <Icon name="plus" size={16} />
              {t('grading.draftNewVersion')}
            </button>
          )}
        </div>
      </header>

      {!editable && (
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {strategy.status === 'ACTIVE' ? t('grading.readOnlyActive') : t('grading.readOnlySuperseded')}
        </p>
      )}

      <Panel title={t('grading.scalePanel')} icon="academic">
        <BandScale
          input={{
            minScore: numbersReadable ? shape.minScore : 0,
            maxScore: numbersReadable ? shape.maxScore : 0,
            passThreshold: numbersReadable ? shape.passThreshold : 0,
            decimalPlaces: shape.decimalPlaces,
            bands: numbersReadable ? shape.bands : [],
          }}
          title={scaleTitle}
        />

        {!numbersReadable && (
          <p className={gov.jsonError} role="alert">
            {t('grading.numbersUnreadable')}
          </p>
        )}

        {numbersReadable &&
          (issues.length === 0 ? (
            <p className={gov.liveReportOk} role="status">
              {t('grading.liveValid')}
            </p>
          ) : (
            <div className={gov.liveReport} role="alert">
              <strong>{t('grading.liveInvalid', { total: issues.length })}</strong>
              <ul className={gov.liveReportList}>
                {issues.map((issue, index) => (
                  <li key={`${issue.code}-${index}`}>
                    {t(`grading.validation.${issue.code}`, issue.params)}
                  </li>
                ))}
              </ul>
            </div>
          ))}

        {serverReport && (
          <div className={serverReport.valid ? gov.liveReportOk : gov.liveReport} role="status">
            <strong>
              {serverReport.valid
                ? t('grading.serverValid')
                : t('grading.serverInvalid', { total: serverReport.errors.length })}
            </strong>
            {!serverReport.valid && (
              <ul className={gov.liveReportList}>
                {/* The server's sentences, verbatim. */}
                {serverReport.errors.map((sentence) => (
                  <li key={sentence}>{sentence}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Panel>

      <Panel title={t('grading.bandsPanel')} icon="checklist"
        actions={
          editable ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() =>
                set('bands', [
                  ...draft.bands,
                  { code: '', label: '', min: '', max: '', gradePoints: '', description: '' },
                ])
              }
            >
              <Icon name="plus" size={15} />
              {t('grading.addBand')}
            </button>
          ) : undefined
        }
      >
        <div className={styles.tableWrap}>
          <table className={gov.editorTable}>
            <caption className={styles.tableCaption}>{t('grading.bandsCaption')}</caption>
            <thead>
              <tr>
                <th scope="col">{t('grading.colCode')}</th>
                <th scope="col">{t('grading.colLabel')}</th>
                <th scope="col">{t('grading.colMin')}</th>
                <th scope="col">{t('grading.colMax')}</th>
                <th scope="col">{t('grading.colPoints')}</th>
                <th scope="col">{t('grading.colDescription')}</th>
                {editable && (
                  <th scope="col">
                    <span className={styles.srOnlyText}>{t('common.actions')}</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {draft.bands.map((band, index) => (
                <tr key={index}>
                  <td>
                    <input className={gov.editorInputNarrow} value={band.code} disabled={!editable}
                      aria-label={t('grading.colCode')}
                      onChange={(event) => setBand(index, { code: event.target.value })} />
                  </td>
                  <td>
                    <input className={gov.editorInput} value={band.label} disabled={!editable}
                      aria-label={t('grading.colLabel')}
                      onChange={(event) => setBand(index, { label: event.target.value })} />
                  </td>
                  <td>
                    <input className={gov.editorInputNarrow} inputMode="decimal" value={band.min} disabled={!editable}
                      aria-label={t('grading.colMin')}
                      aria-invalid={band.min.trim() !== '' && !Number.isFinite(parse(band.min))}
                      onChange={(event) => setBand(index, { min: event.target.value })} />
                  </td>
                  <td>
                    <input className={gov.editorInputNarrow} inputMode="decimal" value={band.max} disabled={!editable}
                      aria-label={t('grading.colMax')}
                      aria-invalid={band.max.trim() !== '' && !Number.isFinite(parse(band.max))}
                      onChange={(event) => setBand(index, { max: event.target.value })} />
                  </td>
                  <td>
                    <input className={gov.editorInputNarrow} inputMode="decimal" value={band.gradePoints} disabled={!editable}
                      aria-label={t('grading.colPoints')}
                      onChange={(event) => setBand(index, { gradePoints: event.target.value })} />
                  </td>
                  <td>
                    <input className={gov.editorInput} value={band.description} disabled={!editable}
                      aria-label={t('grading.colDescription')}
                      onChange={(event) => setBand(index, { description: event.target.value })} />
                  </td>
                  {editable && (
                    <td>
                      <button type="button" className={gov.rowRemoveBtn}
                        onClick={() => set('bands', draft.bands.filter((_, i) => i !== index))}>
                        {t('common.remove')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title={t('grading.metadataPanel')} icon="settings">
        <div className={gov.metaGrid}>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-name">{t('grading.fieldName')}</label>
            <input id="strategy-name" className={gov.editorInput} value={draft.name} disabled={!editable}
              onChange={(event) => set('name', event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-model">{t('grading.fieldModel')}</label>
            <Select id="strategy-model" value={draft.gradingModel} disabled={!editable}
              onChange={(next) => set('gradingModel', next as GradingModel)}
              options={GRADING_MODELS.map((value) => ({ value, label: t(`grading.models.${value}`, { defaultValue: value }) }))} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-scale">{t('grading.fieldScale')}</label>
            <Select id="strategy-scale" value={draft.gradingScale} disabled={!editable}
              onChange={(next) => set('gradingScale', next as GradingScale)}
              options={GRADING_SCALES.map((value) => ({ value, label: t(`grading.scales.${value}`, { defaultValue: value }) }))} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-min">{t('grading.fieldMin')}</label>
            <input id="strategy-min" className={gov.editorInputNarrow} inputMode="decimal" value={draft.minScore} disabled={!editable}
              onChange={(event) => set('minScore', event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-max">{t('grading.fieldMax')}</label>
            <input id="strategy-max" className={gov.editorInputNarrow} inputMode="decimal" value={draft.maxScore} disabled={!editable}
              onChange={(event) => set('maxScore', event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-threshold">{t('grading.fieldThreshold')}</label>
            <input id="strategy-threshold" className={gov.editorInputNarrow} inputMode="decimal" value={draft.passThreshold} disabled={!editable}
              onChange={(event) => set('passThreshold', event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-decimals">{t('grading.fieldDecimals')}</label>
            <input id="strategy-decimals" className={gov.editorInputNarrow} inputMode="numeric" value={draft.decimalPlaces} disabled={!editable}
              onChange={(event) => set('decimalPlaces', event.target.value)} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-rounding">{t('grading.fieldRounding')}</label>
            <Select id="strategy-rounding" value={draft.rounding} disabled={!editable}
              onChange={(next) => set('rounding', next as RoundingMode)}
              options={ROUNDING_MODES.map((value) => ({ value, label: t(`grading.rounding.${value}`) }))} />
          </div>
          <div className={gov.fieldStack}>
            <label className={styles.fieldLabel} htmlFor="strategy-missing">{t('grading.fieldMissing')}</label>
            <Select id="strategy-missing" value={draft.missingResultRule} disabled={!editable}
              onChange={(next) => set('missingResultRule', next as MissingResultRule)}
              options={MISSING_RESULT_RULES.map((value) => ({
                value,
                label: t(`grading.missing.${value}`),
                description: t(`grading.missingHint.${value}`),
              }))} />
          </div>
        </div>

        <div className={gov.fieldStack} style={{ marginTop: '0.75rem' }}>
          <label className={styles.fieldLabel} htmlFor="strategy-description">{t('grading.fieldDescription')}</label>
          <textarea id="strategy-description" className={styles.textarea} rows={2} value={draft.description} disabled={!editable}
            onChange={(event) => set('description', event.target.value)} />
        </div>

        <fieldset className={styles.fieldset} style={{ marginTop: '0.75rem' }}>
          <legend className={styles.fieldLabel}>{t('grading.fieldTypes')}</legend>
          <p className={styles.fieldHint}>{t('grading.fieldTypesHint')}</p>
          <div className={gov.checkboxGrid}>
            {INSTITUTION_TYPES.map((type) => (
              <Checkbox
                key={type}
                label={t(`admin.profile.institutionTypes.${type}`, { defaultValue: type })}
                checked={draft.applicableInstitutionTypes.includes(type)}
                disabled={!editable}
                onChange={(event) =>
                  set(
                    'applicableInstitutionTypes',
                    event.target.checked
                      ? [...draft.applicableInstitutionTypes, type]
                      : draft.applicableInstitutionTypes.filter((value) => value !== type),
                  )
                }
              />
            ))}
          </div>
        </fieldset>
      </Panel>

      <Panel title={t('grading.honoursPanel')} icon="star"
        actions={
          editable ? (
            <button type="button" className={styles.secondaryBtn}
              onClick={() => set('honours', [...draft.honours, { classification: '', minAverage: '' }])}>
              <Icon name="plus" size={15} />
              {t('grading.addHonours')}
            </button>
          ) : undefined
        }
      >
        {draft.honours.length === 0 ? (
          <p className={styles.sectionEmpty} role="status">{t('grading.noHonours')}</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={gov.editorTable}>
              <thead>
                <tr>
                  <th scope="col">{t('grading.colClassification')}</th>
                  <th scope="col">{t('grading.colMinAverage')}</th>
                  {editable && (
                    <th scope="col"><span className={styles.srOnlyText}>{t('common.actions')}</span></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {draft.honours.map((rule, index) => (
                  <tr key={index}>
                    <td>
                      <input className={gov.editorInput} value={rule.classification} disabled={!editable}
                        aria-label={t('grading.colClassification')}
                        onChange={(event) => setHonours(index, { classification: event.target.value })} />
                    </td>
                    <td>
                      <input className={gov.editorInputNarrow} inputMode="decimal" value={rule.minAverage} disabled={!editable}
                        aria-label={t('grading.colMinAverage')}
                        onChange={(event) => setHonours(index, { minAverage: event.target.value })} />
                    </td>
                    {editable && (
                      <td>
                        <button type="button" className={gov.rowRemoveBtn}
                          onClick={() => set('honours', draft.honours.filter((_, i) => i !== index))}>
                          {t('common.remove')}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ConfirmActionDialog
        open={activating}
        title={t('grading.activateTitle', { code: strategy.code, n: strategy.version })}
        intro={t('grading.activateIntro')}
        tone="warning"
        impactStatus="ready"
        impact={
          <ul className={styles.impactList}>
            <li>{t('grading.activateImpactSupersede', { code: strategy.code })}</li>
            <li>{t('grading.activateImpactImmutable')}</li>
          </ul>
        }
        confirmLabel={t('grading.activate')}
        submitting={saving}
        error={activateError}
        onCancel={() => {
          setActivating(false);
          setActivateError(null);
        }}
        onConfirm={activate}
      />

      <ConfirmActionDialog
        open={deleting}
        title={t('grading.deleteTitle', { code: strategy.code, n: strategy.version })}
        intro={t('grading.deleteIntro')}
        tone="critical"
        impactStatus="ready"
        impact={<p className={styles.rowDetail}>{t('grading.deleteImpact')}</p>}
        confirmPhrase={strategy.code}
        confirmLabel={t('grading.deleteDraft')}
        submitting={saving}
        error={deleteError}
        onCancel={() => {
          setDeleting(false);
          setDeleteError(null);
        }}
        onConfirm={() => void remove()}
      />

      <p className={styles.footnote}>
        {t('grading.reloadHint')}{' '}
        <button type="button" className={styles.linkBtn} onClick={reload}>
          {t('common.retry')}
        </button>
      </p>
    </>
  );
}
